import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { token } from '@flipster/styled-system/tokens';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

type BreakpointType = 'mobile' | 'tablet' | 'smallDesktop' | 'largeDesktop';

const BreakpointMapper: { [key in BreakpointType]: string } = {
  mobile: `(max-width: calc(${token('breakpoints.tablet')} - 1px))`,
  tablet: `(min-width: ${token('breakpoints.tablet')}) and (max-width: calc(${token('breakpoints.smallDesktop')} - 1px))`,
  smallDesktop: `(min-width: ${token('breakpoints.smallDesktop')}) and (max-width: calc(${token('breakpoints.largeDesktop')} - 1px))`,
  largeDesktop: `(min-width: ${token('breakpoints.largeDesktop')})`,
} as const;

function getCurrentBreakpoint(): BreakpointType | null {
  for (const [key, mediaQuery] of Object.entries(BreakpointMapper)) {
    if (window.matchMedia(mediaQuery).matches) {
      return key as BreakpointType;
    }
  }
  return null;
}

export const breakpointBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const breakpointAtom = atom<BreakpointType | null>(null);

  const booleanBreakpointAtom = atom((get) => {
    const breakpoint = get(breakpointAtom);
    const isMobile = breakpoint === 'mobile';
    const isTablet = breakpoint === 'tablet';
    const isSmallDesktop = breakpoint === 'smallDesktop';
    const isLargeDesktop = breakpoint === 'largeDesktop';
    const isDesktop = isSmallDesktop || isLargeDesktop;
    return {
      isMobile,
      isTablet,
      isSmallDesktop,
      isLargeDesktop,
      isDesktop,
    };
  });

  bunja.effect(() => {
    const handler = () => store.set(breakpointAtom, getCurrentBreakpoint());
    handler();
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
    };
  });

  return {
    breakpointAtom,
    booleanBreakpointAtom,
  };
});

export function useBreakpoint() {
  const { breakpointAtom, booleanBreakpointAtom } = useBunja(breakpointBunja);
  const breakpoint = useAtomValue(breakpointAtom);
  const booleanBreakpoint = useAtomValue(booleanBreakpointAtom);

  return useMemo(() => ({ breakpoint, ...booleanBreakpoint }), [breakpoint, booleanBreakpoint]);
}
